import { useState } from "react";
import { toast } from "sonner";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

export const PlanRequestModal = ({ open, pkg, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !pkg) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/plan-requests", {
        plan_lookup_key: pkg.lookup_key,
        name: form.name.trim(),
        email: form.email.trim(),
        business_name: form.business_name.trim() || null,
        notes: form.notes.trim() || null,
      });
      setDone(true);
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setForm({ name: "", email: "", business_name: "", notes: "" });
    setDone(false);
    onClose();
  };

  const inputCls =
    "w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none transition-colors duration-300";

  return (
    <div
      data-testid="plan-request-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#1e3d3a]/50 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-[#f4f1e8] border border-[#1e3d3a]/12 p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="plan-request-close"
          onClick={close}
          className="absolute right-5 top-5 text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="plan-request-success">
            <CheckCircle2 className="h-12 w-12 text-[#6b7c3a] mx-auto" strokeWidth={1.5} />
            <h3 className="mt-5 font-head text-2xl text-[#1e3d3a]">Request received</h3>
            <p className="mt-3 text-sm text-[#1e3d3a]/65 leading-relaxed">
              Thanks, {form.name.split(" ")[0] || "there"}. Our team will review your <strong>{pkg.name}</strong> request
              and email you a secure link to activate your plan — usually within one business day.
            </p>
            <button
              data-testid="plan-request-done"
              onClick={close}
              className="mt-6 rounded-full bg-[#1e3d3a] text-white px-6 py-3 text-sm font-medium hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f] font-bold">{pkg.name}</p>
            <h3 className="mt-3 font-head text-2xl text-[#1e3d3a]">Request your {pkg.name} plan</h3>
            <p className="mt-2 text-sm text-[#1e3d3a]/60 leading-relaxed">
              Tell us a little about your business. We'll tailor your {pkg.name} engagement and email you a secure
              link to get started — no payment now.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                data-testid="plan-request-name"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Your name *"
              />
              <input
                data-testid="plan-request-email"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="Email *"
              />
              <input
                data-testid="plan-request-business"
                className={inputCls}
                value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)}
                placeholder="Business name"
              />
              <textarea
                data-testid="plan-request-notes"
                className={`${inputCls} resize-none`}
                rows={3}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Anything we should know? (optional)"
              />
              <button
                type="submit"
                data-testid="plan-request-submit"
                disabled={submitting}
                className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-medium tracking-wide hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                ) : (
                  `Request ${pkg.name}`
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
